import { History } from "lucide-react";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { EmptyState } from "@/components/ui/feedback";
import { NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { WipHint } from "@/components/ui/wip-hint";
import { SectionTitle } from "@/features/oversight/oversight-ui";

/**
 * Chain Monitor (MON-01, `/monitoring/chain`).
 *
 * Work-in-progress layout port (design-ref 2026-10-03, task r2-monitoring-chain).
 * The uat design is `.design-ref/prototype/features/oversight/monitoring.tsx`
 * → `ChainMonitor`: a breadcrumb, a six-field chain summary panel, an "Active
 * Watches" table and a "Checkpoint History" timeline. That structure and its
 * classes are carried over here; its data is not.
 *
 * The prototype reads `CHAINS[0]` from a static fixture — a mainnet block
 * height, a finality threshold, a reorg count of 0, watch rows and checkpoint
 * history, none of it ever collected. The earlier defer note on this route
 * records the same thing (page-delivery-pipeline.md G6): a block height and a
 * "0 reorgs" cell read as live mainnet observations, which is the
 * fabricated-UI case G6 names explicitly. There is still no chain-health
 * endpoint in `src/lib/api.ts` (`docs/FRONTEND_API_INTEGRATION_MATRIX.md`:
 * Monitoring "Existing API source: none"), so every field renders the shared
 * `UnavailableValue` marker, the watches table renders its designed empty
 * state, and the checkpoint history renders a designed empty state instead of
 * a chart. Nothing here is estimated.
 *
 * The internal contract is not empty —
 * `packages/contracts/generated/alloy/chain/v1/chain_runtime_pb.ts` carries
 * `alloy.chain.v1.ChainMonitorService` (`GetChainMonitorRuntime`,
 * `GetMonitorCheckpoints`, `ListWatches`, `ListHistoricalReads`) — but that is
 * internal gRPC, and this app wires REST through the gateway only. The missing
 * piece is the gateway route plus the read it would serve.
 */

/** Chain summary fields, in the designer's order. Values are not available. */
const CHAIN_FIELDS = [
  "Chain",
  "Status",
  "Block Height",
  "Last Observation",
  "Finality Threshold",
  "Reorgs (24h)",
] as const;

/** Active-watches columns, in the designer's order. */
const WATCH_COLUMNS = ["Watch ID", "Address", "Type", "Since", "Last Event"] as const;

export default function MonitoringChainPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Monitoring", href: "/monitoring" }, { label: "Chain Monitor" }]} />

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Chain Monitor</h1>
        <WipHint />
      </div>
      <p className="mb-5 max-w-[720px] text-sm leading-relaxed text-muted">
        This area has no backend in this environment yet, so no chain state is shown or estimated.
      </p>

      <NotAvailableState
        title="Chain monitoring is not available yet"
        description="Block height, finality, reorg count, watches and checkpoints are not available in this app, so no chain state is shown. Nothing here is estimated."
      />

      <SectionTitle className="mt-6">Chain Status</SectionTitle>
      <div className="mb-6 grid grid-cols-2 gap-5 rounded-lg border border-line bg-surface px-5 py-4 sm:grid-cols-3">
        {CHAIN_FIELDS.map((label) => (
          <div key={label}>
            <div className="text-xs text-muted">{label}</div>
            <div className="mt-1 text-[15px] font-bold text-ink">
              <UnavailableValue />
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Active Watches</SectionTitle>
      <TableWrapper className="mb-6">
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              {WATCH_COLUMNS.map((column) => (
                <th key={column} className="px-4 py-2.5">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={WATCH_COLUMNS.length}
              title="No watches have been recorded"
              description="Chain watches are collected by the chain-health read model, which this environment does not run, so there is nothing to list."
            />
          </tbody>
        </Table>
      </TableWrapper>

      <SectionTitle>Checkpoint History</SectionTitle>
      <EmptyState
        icon={<History aria-hidden className="size-5 text-muted" />}
        title="No checkpoints have been recorded"
        description="Finality checkpoints and reorg events are not collected in this environment, so there is no history to plot."
        meta="Nothing on this page is estimated from block time, transfers, or any other source."
      />
    </div>
  );
}
